import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Egg, ArrowLeft, Users, GripVertical, CheckCircle2, XCircle,
  ArrowUpDown, Baby, ListPlus, Loader2, DollarSign, Calendar,
} from 'lucide-react';

const STATUS_LABELS = {
  planned: 'Planeada', paired: 'Emparejada', ovulating: 'Ovulando',
  laid: 'Puesta', incubating: 'Incubando', hatching: 'Por nacer',
  available: 'Disponible', assigned: 'Asignada', completed: 'Completada',
};

const STATUS_COLORS = {
  planned: 'text-zinc-400', paired: 'text-blue-400', ovulating: 'text-purple-400',
  laid: 'text-amber-400', incubating: 'text-orange-400', hatching: 'text-emerald-400',
  available: 'text-emerald-300', assigned: 'text-teal-400', completed: 'text-zinc-500',
};

const ALL_STATES = ['planned','paired','ovulating','laid','incubating','hatching','available','assigned','completed'];

export default function ClutchDetailPage() {
  const { clutchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clutch, setClutch] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [isBreeder, setIsBreeder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hatchOpen, setHatchOpen] = useState(false);
  const [hatchForm, setHatchForm] = useState({ actual_count: '', hatch_notes: '', offspring: [] });
  const [converting, setConverting] = useState(false);

  const load = async () => {
    try {
      const data = await api.getClutch(clutchId);
      setClutch(data);
      const wl = await api.getClutchWaitlist(clutchId);
      setWaitlist(wl.waitlist || []);
      setIsBreeder(wl.is_breeder);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [clutchId]);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.updateClutchStatus(clutchId, newStatus);
      toast.success(`Estado cambiado a: ${STATUS_LABELS[newStatus]}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleJoinWaitlist = async () => {
    try {
      await api.joinClutchWaitlist(clutchId, { notes: '', deposit_intent: false });
      toast.success('Te uniste a la waitlist');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleHatch = async () => {
    try {
      await api.recordHatch(clutchId, {
        actual_count: parseInt(hatchForm.actual_count) || 0,
        hatch_notes: hatchForm.hatch_notes,
        offspring: hatchForm.offspring,
      });
      toast.success('Hatch registrado');
      setHatchOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAssign = async (offspringIndex, waitlistId) => {
    try {
      await api.assignOffspring(clutchId, { offspring_index: offspringIndex, waitlist_id: waitlistId });
      toast.success('Cria asignada');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const result = await api.convertToListings(clutchId);
      toast.success(`${result.created} listings creados`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConverting(false); }
  };

  const addOffspringRow = () => {
    setHatchForm(f => ({ ...f, offspring: [...f.offspring, { morph: '', sex: 'unknown', weight: '' }] }));
  };

  if (loading) return <div className="text-zinc-500 text-center py-12">Cargando...</div>;
  if (!clutch) return <div className="text-zinc-500 text-center py-12">Camada no encontrada</div>;

  const alreadyInWaitlist = waitlist.some(w => w.user_id === user?.id);

  return (
    <div data-testid="clutch-detail-page" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-light text-zinc-100 font-[Outfit]">
            <Egg className="w-6 h-6 inline mr-2 text-emerald-400" />
            {clutch.species_name} {clutch.morph_expected}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-sm ${STATUS_COLORS[clutch.status]}`}>{STATUS_LABELS[clutch.status]}</span>
            <span className="text-xs text-zinc-500">por {clutch.breeder_name}</span>
            {clutch.estimated_hatch_date && <span className="text-xs text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{clutch.estimated_hatch_date}</span>}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="glass-card p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><span className="text-zinc-500">Padres:</span> <span className="text-zinc-200">{clutch.sire_info} x {clutch.dam_info}</span></div>
          <div><span className="text-zinc-500">Cantidad esperada:</span> <span className="text-zinc-200">{clutch.expected_count}</span></div>
          <div><span className="text-zinc-500">Precio rango:</span> <span className="text-zinc-200">${clutch.price_range_min} - ${clutch.price_range_max} MXN</span></div>
          {clutch.genetic_probability && <div className="col-span-2"><span className="text-zinc-500">Probabilidad:</span> <span className="text-zinc-200">{clutch.genetic_probability}</span></div>}
          {clutch.breeder_notes && <div className="col-span-3"><span className="text-zinc-500">Notas:</span> <span className="text-zinc-200">{clutch.breeder_notes}</span></div>}
        </div>
        {clutch.parent_images?.length > 0 && (
          <div className="flex gap-2 mt-4">
            {clutch.parent_images.map((img, i) => (
              <img key={img} src={img} alt="" className="w-24 h-24 rounded-lg object-cover" />
            ))}
          </div>
        )}
      </div>

      {/* Breeder Controls */}
      {isBreeder && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Control del Breeder</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {ALL_STATES.map(s => (
              <Button key={s} size="sm" variant={clutch.status === s ? 'default' : 'outline'}
                className={clutch.status === s ? 'bg-emerald-600 text-white' : 'border-white/10 text-zinc-400'}
                onClick={() => handleStatusChange(s)} data-testid={`status-btn-${s}`}>
                {STATUS_LABELS[s]}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setHatchOpen(true); addOffspringRow(); }} variant="outline" className="border-emerald-500/20 text-emerald-400" data-testid="hatch-btn">
              <Baby className="w-4 h-4 mr-2" />Registrar Hatch
            </Button>
            {clutch.offspring?.some(o => o.status === 'available') && (
              <Button onClick={handleConvert} disabled={converting} variant="outline" className="border-blue-500/20 text-blue-400" data-testid="convert-btn">
                {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ListPlus className="w-4 h-4 mr-2" />}
                Convertir a Listings
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Offspring */}
      {clutch.offspring?.length > 0 && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-medium text-zinc-100 mb-4">Crias ({clutch.offspring.length})</h2>
          <div className="space-y-2">
            {clutch.offspring.map((o) => (
              <div key={o.id || o.morph} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div>
                  <span className="text-zinc-200 text-sm">{o.morph || 'Sin morph'}</span>
                  <span className="text-xs text-zinc-500 ml-2">{o.sex} {o.weight && `· ${o.weight}`}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'available' ? 'bg-emerald-500/10 text-emerald-400' : o.status === 'assigned' ? 'bg-blue-500/10 text-blue-400' : 'bg-zinc-500/10 text-zinc-400'}`}>
                    {o.status === 'available' ? 'Disponible' : o.status === 'assigned' ? 'Asignada' : o.status === 'listed' ? 'Publicada' : o.status}
                  </span>
                  {isBreeder && o.status === 'available' && waitlist.length > 0 && (
                    <Select onValueChange={wlId => handleAssign(i, wlId)}>
                      <SelectTrigger className="w-36 bg-white/5 border-white/10 text-zinc-100 h-8 text-xs">
                        <SelectValue placeholder="Asignar a..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121215] border-white/10">
                        {waitlist.filter(w => w.status === 'active').map(w => (
                          <SelectItem key={w.id} value={w.id}>#{w.position} {w.user_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waitlist */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-zinc-100">
            <Users className="w-5 h-5 inline mr-2 text-emerald-400" />
            Waitlist ({clutch.waitlist_count || 0})
          </h2>
          {!isBreeder && !alreadyInWaitlist && clutch.status !== 'completed' && (
            <Button onClick={handleJoinWaitlist} className="bg-emerald-600 text-white" data-testid="join-waitlist-btn">
              Unirme a la lista
            </Button>
          )}
          {alreadyInWaitlist && !isBreeder && (
            <span className="text-sm text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> En waitlist</span>
          )}
        </div>
        <div className="space-y-2">
          {waitlist.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">Sin entradas en la waitlist</p>
          ) : waitlist.map((w) => (
            <div key={w.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5" data-testid={`waitlist-${w.id}`}>
              {isBreeder && <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab" />}
              <span className="w-8 text-center text-sm font-mono text-zinc-500">#{w.position}</span>
              <div className="flex-1 min-w-0">
                <span className="text-zinc-200 text-sm">{w.user_name}</span>
                {w.deposit_status === 'confirmed' && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <DollarSign className="w-3 h-3 inline" /> Deposito
                  </span>
                )}
              </div>
              <span className="text-xs text-zinc-500">{new Date(w.joined_at).toLocaleDateString('es-MX')}</span>
              {w.status === 'assigned' && <span className="text-xs text-blue-400">Asignado</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Hatch Dialog */}
      <Dialog open={hatchOpen} onOpenChange={setHatchOpen}>
        <DialogContent className="max-w-lg bg-[#121215] border-white/10 text-zinc-100 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Registrar Hatch</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-zinc-400 text-xs">Cantidad nacida</span>
                <Input type="number" value={hatchForm.actual_count} onChange={e => setHatchForm(f => ({ ...f, actual_count: e.target.value }))}
                  className="bg-white/5 border-white/10 text-zinc-100" data-testid="hatch-count" />
              </div>
            </div>
            <div>
              <span className="text-zinc-400 text-xs">Notas del hatch</span>
              <Input value={hatchForm.hatch_notes} onChange={e => setHatchForm(f => ({ ...f, hatch_notes: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-300 text-sm">Crias individuales</span>
                <Button size="sm" variant="ghost" onClick={addOffspringRow} className="text-emerald-400">+ Agregar</Button>
              </div>
              {hatchForm.offspring.map((o, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Morph" value={o.morph} onChange={e => {
                    const arr = [...hatchForm.offspring]; arr[i].morph = e.target.value; setHatchForm(f => ({ ...f, offspring: arr }));
                  }} className="bg-white/5 border-white/10 text-zinc-100 flex-1" />
                  <Select value={o.sex} onValueChange={v => {
                    const arr = [...hatchForm.offspring]; arr[i].sex = v; setHatchForm(f => ({ ...f, offspring: arr }));
                  }}>
                    <SelectTrigger className="w-28 bg-white/5 border-white/10 text-zinc-100"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121215] border-white/10">
                      <SelectItem value="unknown">?</SelectItem>
                      <SelectItem value="male">Macho</SelectItem>
                      <SelectItem value="female">Hembra</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Peso" value={o.weight} onChange={e => {
                    const arr = [...hatchForm.offspring]; arr[i].weight = e.target.value; setHatchForm(f => ({ ...f, offspring: arr }));
                  }} className="bg-white/5 border-white/10 text-zinc-100 w-20" />
                </div>
              ))}
            </div>
            <Button onClick={handleHatch} className="w-full bg-emerald-600 text-white" data-testid="confirm-hatch">
              <Baby className="w-4 h-4 mr-2" />Confirmar Hatch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
